/**
 * App wall clock: Turkey (Europe/Istanbul, permanent UTC+3 since 2016).
 * Keep in sync with backend/src/utils/appTime.ts
 */

export const APP_TIMEZONE = "Europe/Istanbul";
export const APP_UTC_OFFSET = "+03:00";

export function dateStrInAppTz(d) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const m = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${m.year}-${m.month}-${m.day}`;
}

export function dayBoundsInAppTz(dateStr) {
  const start = new Date(`${dateStr}T00:00:00.000${APP_UTC_OFFSET}`);
  const end = new Date(`${dateStr}T23:59:59.999${APP_UTC_OFFSET}`);
  return { start, end };
}

export function noonInAppTzDate(dateStr) {
  return new Date(`${dateStr}T12:00:00.000${APP_UTC_OFFSET}`);
}

export function toStoredCalendarDate(dateStr) {
  return noonInAppTzDate(dateStr);
}

export function formatCalendarDateShortTr(dateStr) {
  if (!dateStr) return "—";
  const s = String(dateStr).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("tr-TR", {
      timeZone: APP_TIMEZONE,
      dateStyle: "short",
    }).format(d);
  }
  const d = new Date(`${m[1]}-${m[2]}-${m[3]}T12:00:00.000${APP_UTC_OFFSET}`);
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: APP_TIMEZONE,
    dateStyle: "short",
  }).format(d);
}

export function normalizeQueryToYyyyMmDd(s) {
  const t = String(s).trim();
  const plain = t.match(/^(\d{4}-\d{2}-\d{2})/);
  if (plain) return plain[1];
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return t.slice(0, 10);
  return dateStrInAppTz(d);
}

export function normalizeInputToAppYyyyMmDd(input) {
  if (input instanceof Date) return dateStrInAppTz(input);
  const t = String(input).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return dateStrInAppTz(new Date(t));
}

export function defaultStartDateStrBeforeEnd(endYyyyMmDd, daysBack) {
  const anchor = noonInAppTzDate(endYyyyMmDd);
  const shifted = new Date(anchor.getTime() - daysBack * 86400000);
  return dateStrInAppTz(shifted);
}

export function addCalendarDaysToDateStr(dateStr, deltaDays) {
  const anchor = noonInAppTzDate(dateStr);
  const shifted = new Date(anchor.getTime() + deltaDays * 86400000);
  return dateStrInAppTz(shifted);
}

const pad2 = (n) => String(n).padStart(2, "0");

export function combineYyyyMmDdAndMinsInAppTz(dateStr, minuteOfDay) {
  let mins = minuteOfDay;
  let dStr = dateStr;
  while (mins >= 24 * 60) {
    mins -= 24 * 60;
    dStr = addCalendarDaysToDateStr(dStr, 1);
  }
  while (mins < 0) {
    mins += 24 * 60;
    dStr = addCalendarDaysToDateStr(dStr, -1);
  }
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return new Date(`${dStr}T${pad2(h)}:${pad2(m)}:00.000${APP_UTC_OFFSET}`);
}

export function weekdayKeyInAppTz(d) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: APP_TIMEZONE,
    weekday: "long",
  })
    .format(d)
    .toLowerCase();
}

export function minutesSinceMidnightInAppTz(d) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: APP_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return hour * 60 + minute;
}

export function formatTimeTrInAppTz(isoStr) {
  if (isoStr == null || isoStr === "") return "--:--:--";
  const d = isoStr instanceof Date ? isoStr : new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "--:--:--";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: APP_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

export function formatDateTimeTrInAppTz(isoStr) {
  if (isoStr == null || isoStr === "") return "-";
  const d = isoStr instanceof Date ? isoStr : new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: APP_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
}

export function formatInstantDateShortTr(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: APP_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

export function formatTimeHmTrInAppTz(d = new Date()) {
  return new Intl.DateTimeFormat("tr-TR", {
    timeZone: APP_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function ymKeyInAppTz(d) {
  return dateStrInAppTz(d).slice(0, 7);
}

export function addMonthsToYmKey(ym, deltaMonths) {
  const [y, m] = ym.split("-").map(Number);
  const idx = y * 12 + (m - 1) + deltaMonths;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

export function yearInAppTz(d) {
  return Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: APP_TIMEZONE,
      year: "numeric",
    }).format(d),
  );
}

export function yearBoundsInAppTz(year) {
  return {
    start: new Date(`${year}-01-01T00:00:00.000${APP_UTC_OFFSET}`),
    end: new Date(`${year}-12-31T23:59:59.999${APP_UTC_OFFSET}`),
  };
}
